import React, { useEffect, useMemo, useState } from 'react';
import { Check, Users, AlertCircle } from 'lucide-react';

export interface SpeakerGroup {
  displayName: string;
  segmentCount: number;
  segmentId: number;
}

interface ManageSpeakersModalProps {
  speakerGroups: SpeakerGroup[];
  onRenameSpeaker: (segmentId: number, newSpeaker: string) => Promise<boolean>;
}

const speakerNameMaxLength = 255;

// --- Helpers for Avatar ---

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name: string) => {
  const colors = [
    'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800/30',
    'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800/30',
    'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800/30',
    'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800/30',
    'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/30',
    'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400 border-teal-200 dark:border-teal-800/30',
    'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800/30',
    'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800/30',
    'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800/30',
    'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400 border-violet-200 dark:border-violet-800/30',
    'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800/30',
    'bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/30 dark:text-fuchsia-400 border-fuchsia-200 dark:border-fuchsia-800/30',
    'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400 border-pink-200 dark:border-pink-800/30',
    'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200 dark:border-rose-800/30',
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const SpeakerRow: React.FC<{
  group: SpeakerGroup;
  onSave: (group: SpeakerGroup, newName: string) => Promise<boolean>;
}> = ({ group, onSave }) => {
  const [value, setValue] = useState(group.displayName);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Sync value if prop changes (external update)
  useEffect(() => {
    setValue(group.displayName);
  }, [group.displayName]);

  // Reset success state after a delay
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => setIsSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  const hasChanged = value.trim() !== group.displayName && value.trim().length > 0;

  const handleSave = async () => {
    if (!hasChanged || isSaving) return;
    
    setIsSaving(true);
    const success = await onSave(group, value.trim());
    setIsSaving(false);
    
    if (success) {
      setIsSuccess(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setValue(group.displayName);
    }
  };

  const currentInitials = getInitials(value);
  const currentColorClass = getAvatarColor(value);

  return (
    <div className="group flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 dark:bg-zinc-800/50 dark:hover:bg-zinc-800 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-zinc-700">
      <div className="mt-2.5 shrink-0">
        <div className={`h-8 w-8 rounded-full flex items-center justify-center shadow-sm border text-xs font-bold transition-colors duration-200 ${currentColorClass}`}>
          {currentInitials}
        </div>
      </div>
      
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center justify-between">
          <label 
            htmlFor={`rename-speaker-${group.segmentId}`}
            className="text-xs font-medium text-slate-500 dark:text-zinc-400"
          >
            {group.segmentCount} segment{group.segmentCount === 1 ? '' : 's'}
          </label>
        </div>
        
        <div className="relative">
          <input
            id={`rename-speaker-${group.segmentId}`}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            maxLength={speakerNameMaxLength}
            className={`
              block w-full rounded-md border-0 py-2 pl-3 pr-12
              text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300
              placeholder:text-slate-400 
              focus:ring-2 focus:ring-inset focus:ring-indigo-600
              dark:bg-zinc-900 dark:text-white dark:ring-zinc-700 dark:focus:ring-indigo-500
              sm:text-sm sm:leading-6
              disabled:opacity-50 transition-all
              ${isSuccess ? 'ring-emerald-500 focus:ring-emerald-500 dark:ring-emerald-500/50' : ''}
            `}
          />
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
            <button
              type="button"
              className={`
                h-7 w-7 rounded-md flex items-center justify-center transition-colors
                ${isSuccess
                  ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                  : hasChanged
                    ? 'text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 cursor-pointer'
                    : 'text-slate-300 dark:text-zinc-600 pointer-events-none'
                }
              `}
              disabled={isSaving || (!hasChanged && !isSuccess)}
              onClick={handleSave}
              title={hasChanged ? "Save" : isSuccess ? "Saved" : "No changes"}
            >
              {isSaving ? (
                <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className={`h-4 w-4 ${isSuccess ? 'stroke-[3]' : ''}`} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ManageSpeakersModal: React.FC<ManageSpeakersModalProps> = ({
  speakerGroups,
  onRenameSpeaker,
}) => {
  const sortedGroups = useMemo(() => {
    return [...speakerGroups].sort((a, b) => {
      // Sort by segment count (desc) then name (asc)
      if (b.segmentCount !== a.segmentCount) return b.segmentCount - a.segmentCount;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [speakerGroups]);

  const handleRename = async (group: SpeakerGroup, newName: string): Promise<boolean> => {
    if (newName === group.displayName) return true;
    return await onRenameSpeaker(group.segmentId, newName);
  };

  return (
    <div className="w-80 sm:w-96 rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
      <div className="p-4 border-b border-slate-100 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/50">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-4 w-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Speaker Identification</h3>
        </div>
        <p className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
          Rename speakers to update all their corresponding segments automatically.
        </p>
      </div>

      <div className="p-2 space-y-1 max-h-[60vh] overflow-y-auto custom-scrollbar">
        {sortedGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <div className="h-10 w-10 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-3">
              <AlertCircle className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
            </div>
            <p className="text-sm font-medium text-slate-900 dark:text-zinc-200">No speakers found</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
              We couldn't detect any distinct speakers in this audio.
            </p>
          </div>
        ) : (
          sortedGroups.map((group) => (
            <SpeakerRow 
              key={`${group.segmentId}-${group.displayName}`} 
              group={group} 
              onSave={handleRename} 
            />
          ))
        )}
      </div>
      
      {sortedGroups.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 dark:bg-zinc-800/50 border-t border-slate-100 dark:border-zinc-800 text-center">
          <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-500 uppercase tracking-wider">
            {sortedGroups.length} Speaker{sortedGroups.length !== 1 ? 's' : ''} Detected
          </span>
        </div>
      )}
    </div>
  );
};

export default ManageSpeakersModal;